/* eslint-disable @typescript-eslint/no-explicit-any */
import {DestroyRef, Injectable} from '@angular/core';
import {MapToolsService} from '../../services/map-tools.service';
import {Observable, catchError, of, tap} from 'rxjs';
import {LookupService} from '../../../common/service/lookup.service';
import {LineTypeLookup} from '../../../common/models/line-type.model';
import {SpatialUtilService} from '../../services/spatial-util.service';
import {PlanModelService} from '../../../common/service/plan-model.service';
import {MapService} from '../../services/map.service';
import {PATTERNS} from 'src/app/common/constants';
import {TraitsService} from '../../../common/service/traits.service';
import {PointTypeLookup} from 'src/app/common/models/point-type.model';
import {NgForm} from '@angular/forms';
import {InterestService} from '../../services/features/interest.service';
import {AlertModalService} from '../../../common/component/modal';
import {LegalParcelTypeLookup} from '../../../common/models/legal-parcel-type.model';
import {FeatureAssociationService} from '../../services/features/feature-association.service';
import {EsriModuleService} from '../../esri-nlr/services/esri-module.service';
import {AnnotationService} from '../../services/features/annotation.service';
import {switchMap} from 'rxjs/operators';
import {NgEventBus} from 'ng-event-bus';
import {FeatureApplyUpdateService} from '../../services/feature-apply-update.service';
import {PolygonUtilService} from '../../services/features/polygon-util.service';
import {AngleUtilService} from '../../services/features/angle/angle-util.service';
import {LineService} from '../../services/features/line.service';
import {AttributeViewerTranslationService} from './attribute-viewer-translation.service';
import {PermissionsService} from '../../../common/service/permissions.service';
import {AttributeViewerContext} from './attribute-viewer-context.model';
import {forkJoinDefaultIfEmpty} from '../../../common/observable.utils';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Injectable({
    providedIn: 'root'
})
export class AttributeViewerEditorService {
    title = 'AttributeViewerEditorService';

    permissions = ['EDIT_DESIGN', 'VIEW_DESIGN'];

    filteredLookups = [];

    context: any;

    private setShortcutsEnabledTimeout = null;

    constructor(
        private alertModalService: AlertModalService,
        private angleUtilService: AngleUtilService,
        private annotationService: AnnotationService,
        private attributeViewerTranslationService: AttributeViewerTranslationService,
        private esriModuleService: EsriModuleService,
        private eventBus: NgEventBus,
        private featureApplyUpdateService: FeatureApplyUpdateService,
        private featureAssociationService: FeatureAssociationService,
        private interestService: InterestService,
        private lineService: LineService,
        private lookupService: LookupService,
        private mapService: MapService,
        private mapToolsService: MapToolsService,
        private permissionsService: PermissionsService,
        private planModelService: PlanModelService,
        private polygonUtilService: PolygonUtilService,
        private spatialUtilService: SpatialUtilService,
        private traitsService: TraitsService,
        private destroyRef: DestroyRef,
    ) {
        this.mapToolsService.init(
            'ATTRIBUTE_EDITOR_WINDOW',
            () => {
                if (!this.isDisabled() && this.context && ['affectedLot', 'interest'].includes(this.context.tool.type)) {
                    this.eventBus.cast(
                        {affectedLot: 'openAffectedLotModal', interest: 'openInterestModal'}[this.context.tool.type],
                        this.context.selectedAttrsList[0]
                    );
                    this.mapToolsService.deactivate('ATTRIBUTE_EDITOR_WINDOW');
                }
            },
            () => {},
            () => this.isDisabled()
        );

        this.eventBus
            .on('nlr:trait-service:traitsChanged')
            .pipe(takeUntilDestroyed(destroyRef))
            .subscribe(() => {
                this.spatialUtilService.setLotAcquisitionTransferActionDefaults();

                if (!this.filteredLookups.length) {
                    return;
                }

                const results = this.filteredLookups.map(lookup => {
                    const lookupValues = this.lookupService.getLookups(lookup.lookupService);
                    return {
                        lookupService: lookup.lookupService,
                        values: this.filterLookupValuesByPlanTraitCriteria(lookupValues)
                    };
                });

                this.filteredLookups = [];
                this.filteredLookups.push(...results);
            });
    }

    setupEditor(context: AttributeViewerContext, typeAttribute?: any) {
        console.debug(`${this.title} - setupEditor`);

        if (!this.mapToolsService.isActive('ATTRIBUTE_EDITOR_WINDOW')) {
            return of(null);
        }

        let typeDisplayOrder = 0;

        if (context.editorAttributes && typeAttribute) {
            //setup editor when type is changed
            typeDisplayOrder = context.editorAttributes.find(attr => attr.prop === 'type').displayOrder;
            for (let i = context.editorAttributes.length - 1; i >= 0; i--) {
                if (context.editorAttributes[i].displayOrder >= typeDisplayOrder) {
                    context.editorAttributes.splice(i, 1);
                }
            }
        } else {
            context.editorAttributes = [];
        }

        if (context.invalidSelectedFeaturesError) {
            context.invalidSelectedFeaturesError = '';
        }

        let selectedFeatureType: any;
        if (context.selectedAttrsList.length > 0 && context.tool.type === 'line') {
            const polylineTypes: LineTypeLookup[] = this.lookupService
                .getLookups('polylineType')
                .filter((l: LineTypeLookup) => context.selectedAttrsList.find(attrs => attrs.type === l.description))
                .map((l: LineTypeLookup) => l.featureType);

            const totalLotBoundaryTypes: LineTypeLookup[] = this.lookupService
                .getLookups('totalBoundaryDistance')
                .filter((l: LineTypeLookup) => context.selectedAttrsList.find(attrs => attrs.type === l.description))
                .map((l: LineTypeLookup) => l.featureType);

            if (polylineTypes.length && totalLotBoundaryTypes.length) {
                selectedFeatureType = context.tool.type;
            } else {
                selectedFeatureType =
                    polylineTypes.length === 1 ? polylineTypes[0] : totalLotBoundaryTypes.length === 1 ? totalLotBoundaryTypes[0] : context.tool.type;
            }
        } else if (context.tool.type === 'controlPoint') {
            selectedFeatureType = 'point';
        } else {
            selectedFeatureType = context.tool.type;
        }

        const lookupObservables: Observable<any>[] = [];

        this.lookupService.getLookups('featureAttributes').forEach((attribute: any) => {
            if (
                attribute.featureType === selectedFeatureType &&
                attribute.editableOrder &&
                attribute.featureClassAttribute === true &&
                attribute.displayOrder >= typeDisplayOrder
            ) {
                context.editorAttributes.push(attribute);

                if (attribute.attributeType === 'DOMAIN' && attribute.lookupService !== null) {
                    if (context.tool.type === 'controlPoint' && attribute.lookupService === 'pointTypeLookups') {
                        attribute.lookupService = 'controlPointTypeLookups';
                    }

                    let lookupValues: any[] = this.getLookupValues(context, attribute);

                    if (attribute.featureSubTypeFilter) {
                        lookupValues = lookupValues.filter((l: any) => context.tool.featureSubTypeIds.split(',').includes(l.id.toString()));
                    }

                    // validate the selected features
                    // Need to have the typeAttribute to check if the user opt in the type
                    if (!this.validateSelectedFeatures(context, attribute, lookupValues, typeAttribute)) {
                        attribute.values = [];
                    } else {
                        // if there is no excludeFromAttributeEditor column, remove any unwanted values
                        if (lookupValues.some(lookupValue => Object.hasOwn(lookupValue, 'businessClass'))) {
                            lookupValues = lookupValues.filter(l => !['Undefined', 'UndefinedControl'].includes(l.businessClass));
                        }

                        if (attribute.prop === 'type' && attribute.featureType === 'line' && lookupValues.some(l => l.validLineTypesOnUpdate)) {
                            const featureMap = this.spatialUtilService.getFeatureMap(this.spatialUtilService.getFeatures(context.featureLayer));
                            const selectedFeatureLineTypes = context.selectedAttrsList
                                .map(v => parseInt(featureMap[v.id].attributes.type))
                                .filter(v => !isNaN(v))
                                .sort((a, b) => a - b)
                                .filter((v, i, a) => i === a.indexOf(v));

                            lookupValues = lookupValues.filter(l => selectedFeatureLineTypes.every(v => l.validLineTypesOnUpdate.includes(v)));
                        }

                        attribute.values = lookupValues;
                    }
                } else if (attribute.attributeType === 'DOMAIN' && attribute.customService !== null) {
                    lookupObservables.push(
                        this.mapService
                            .getCustomLookups(`${attribute.customService}/${this.planModelService.getGuid()}`, attribute)
                            .pipe(tap((lookupValues: any) => (attribute.values = lookupValues)))
                    );
                } else if (attribute.attributeType.includes('RADIO')) {
                    attribute.values = [
                        {value: 'YES', description: 'Yes'},
                        {value: 'NO', description: 'No'}
                    ];
                } else if (attribute.attributeType.includes('STRING')) {
                    if (attribute.dms) {
                        attribute.attributeTypeLength = 9;
                        attribute.attributeType = 'STRING';
                        attribute.pattern = PATTERNS.ANGLE_PATTERN;
                    } else {
                        attribute.attributeTypeLength = this.getAttributeTypeLength(attribute.attributeType);
                    }
                } else if (attribute.attributeType.includes('DECIMAL')) {
                    if (attribute.dms) {
                        attribute.attributeTypeLength = 9;
                        attribute.attributeType = 'STRING';
                        attribute.pattern = new RegExp('^([1-2]?[0-9][0-9]?[0-9]?)?(3[0-6][0-9])?(-[0-5][0-9])?(-[0-5][0-9])?$');
                    } else {
                        const decimalLength = this.getAttributeTypeLength(attribute.attributeType);
                        attribute.pattern = `^-?\\d{1,15}(\\.\\d{1,${decimalLength}})?$`;
                    }
                } else if (attribute.attributeType.includes('INTEGER')) {
                    attribute.attributeTypeLength = this.getAttributeTypeLength(attribute.attributeType);
                    attribute.min = 1;
                    attribute.max = Math.min(Math.pow(10, attribute.attributeTypeLength) - 1, Number.MAX_SAFE_INTEGER);
                    attribute.pattern = '[0-9]+';
                }
            }
        });

        return forkJoinDefaultIfEmpty(lookupObservables).pipe(
            tap(() => {
                if (context.selectedAttrsList.length > 0) {
                    this.updateEditorAttributes(context, typeAttribute, typeDisplayOrder);
                    const parcelTypeAttr = context.editorAttributes.find(a => a.featureType === 'lot' && a.prop === 'type');

                    const acquisitionTransferActionAttr = context.editorAttributes.find(a => a.prop === 'acquisitiontransferaction');
                    if ((typeAttribute ? typeAttribute.value : parcelTypeAttr && parcelTypeAttr.value) && acquisitionTransferActionAttr) {
                        acquisitionTransferActionAttr.values = this.filterLookupValuesBySelectedParcelTypeId(
                            context,
                            acquisitionTransferActionAttr,
                            typeAttribute ? typeAttribute.value.id : parcelTypeAttr.value.id
                        );

                        if (!acquisitionTransferActionAttr.values || !acquisitionTransferActionAttr.values.length) {
                            context.editorAttributes.splice(context.editorAttributes.indexOf(acquisitionTransferActionAttr), 1);
                        }
                    }
                } else {
                    this.resetEditorAttributes(context);
                }
            }),
            catchError(err => {
                console.debug(`${this.title} - setupEditor - error loading lookups:`, err);
                throw new Error('Error loading lookups');
            })
        );
    }

    getLookupValues(context: AttributeViewerContext, attribute: any) {
        const lookupValues: any[] = this.lookupService.getLookups(attribute.lookupService);
        // if there is no planTraitCriteria column, returned the unfiltered values
        if (!lookupValues.some(lookupValue => Object.hasOwn(lookupValue, 'planTraitCriteria'))) {
            return lookupValues;
        }

        const filteredLookups = this.filteredLookups.filter(lookup => lookup.lookupService === attribute.lookupService)[0];

        // return the existing filtered values, if they exist.
        // for points, we always need to filter the results further based on the filterClass
        if (filteredLookups && attribute.featureType !== 'point') {
            return filteredLookups.values;
        }

        let filteredLookupValues = this.filterLookupValuesByPlanTraitCriteria(lookupValues);

        // only applies to points
        filteredLookupValues = this.filterLookupValuesByFilterClassCriteria(context, filteredLookupValues, attribute);

        attribute.values = filteredLookupValues;
        this.filteredLookups.push({lookupService: attribute.lookupService, values: filteredLookupValues});
        return filteredLookupValues;
    }

    filterLookupValuesByPlanTraitCriteria(lookupValues: any[]) {
        return lookupValues.filter(l => this.traitsService.criteriaMatches(l.planTraitCriteria, true));
    }

    private filterLookupValuesBySelectedParcelTypeId(context: AttributeViewerContext, attribute: any, selectedParcelTypeId: any) {
        if (attribute.featureType !== 'lot' || attribute.prop !== 'acquisitiontransferaction') {
            return attribute.values;
        }

        return this.getLookupValues(context, attribute).filter((l: any) => l.validParcelTypeId === selectedParcelTypeId);
    }

    filterLookupValuesByFilterClassCriteria(context: AttributeViewerContext, lookups: any[], attribute: any) {
        // filter values by filterClass (only applicable to point featureType)
        if (attribute.featureType !== 'point' || context.selectedAttrsList.length === 0) {
            return lookups;
        }

        const selectedIds = context.selectedAttrsList.map(attrs => attrs.id);

        const selectedTypes = this.spatialUtilService
            .getFeatures(context.featureLayer)
            .filter((p: any) => selectedIds.includes(p.attributes.id))
            .map((p: any) => p.attributes.type)
            .filter((v: any, i: number, a: any[]) => i === a.indexOf(v));

        const filterClasses = lookups
            .filter(l => selectedTypes.includes(l.id))
            .map(l => l.filterClass)
            .filter((v, i, a) => i === a.indexOf(v));

        if (filterClasses.length === 0) {
            return lookups;
        }

        if (filterClasses.length === 1) {
            return lookups.filter(l => l.filterClass === filterClasses[0]);
        }

        return [];
    }

    // Need to have the typeAttribute to check if the user opt in the type
    validateSelectedFeatures(context: AttributeViewerContext, attribute: any, lookupValues: any[], typeAttribute: any) {
        // Note, this is only specific to types at this stage, but could be extended in the future to other attributes if required
        if (attribute.prop === 'type') {
            const featureMap = this.spatialUtilService.getFeatureMap(this.spatialUtilService.getFeatures(context.featureLayer));
            const uniqueSelectedFeatureIds = context.selectedAttrsList.reduce((acc, v) => {
                const selectedFeatureType = Number.parseInt(featureMap[v.id].attributes.type);
                if (!acc.includes(selectedFeatureType)) {
                    acc.push(selectedFeatureType);
                }
                return acc;
            }, []);

            //There is an existing logic bug since AngularJS (Old)
            //The single selected case logic below should be through only when type of attribute value is undefined.
            //Otherwise, it falls into a infinite loop as it checks old attribute value in the attribute viewer, failing when comparing with updated lookup values
            //While the initial loading (1st time), it's legit, but it should be skipped when the user opt in the type
            if (uniqueSelectedFeatureIds.length === 1 && !typeAttribute?.value) {
                // single selected feature type, set a warning message if the current type is no longer valid
                if (!lookupValues.some(l => l.id === uniqueSelectedFeatureIds[0])) {
                    const type = context.selectedAttrsList[0].type;
                    context.invalidSelectedFeaturesError = `The type '${type}' is not valid for this plan. Please select another value.`;
                }
            } else if (['line', 'polyline', 'totalBoundaryDistance'].includes(attribute.featureType)) {
                // more than one unique feature type is selected, validate that they can be bulk updated
                const validLookupValues = lookupValues.filter(lookupValue =>
                    uniqueSelectedFeatureIds.every((id: any) => lookupValue.validLineTypesOnUpdate.includes(id))
                );

                if (!validLookupValues.length) {
                    context.invalidSelectedFeaturesError =
                        'The line type cannot be updated! The line type may only be bulk updated for lines within' +
                        ' the same class (e.g. cadastral, control, administrative boundary or structure)!';
                }

                return validLookupValues.length;
            } else if (attribute.featureType === 'point') {
                const filterClasses = this.lookupService
                    .getLookups('pointType')
                    .filter((l: PointTypeLookup) => uniqueSelectedFeatureIds.includes(l.id))
                    .map((l: PointTypeLookup) => l.filterClass)
                    .filter((v: PointTypeLookup, i: number, a: PointTypeLookup[]) => i === a.indexOf(v));
                if (filterClasses.length > 1) {
                    context.invalidSelectedFeaturesError =
                        'The point type cannot be updated! The point type may only be bulk updated for points within' +
                        ' the same class (e.g. cadastral, administrative, pointer  or structure)!';
                }
            }
        }

        return true;
    }

    getAttributeTypeLength(attributeType: string) {
        console.debug(`${this.title} - getAttributeTypeLength`);

        const lengthStartIndex = attributeType.indexOf('(') + 1;
        const lengthEndIndex = attributeType.indexOf(')');
        return parseInt(attributeType.substring(lengthStartIndex, lengthEndIndex));
    }

    updateEditorAttributes(context: AttributeViewerContext, typeAttribute: any, typeDisplayOrder: any) {
        console.debug(`${this.title} - updateEditorAttributes`);

        context.editorAttributes
            .filter((attrs: any) => attrs.displayOrder >= typeDisplayOrder)
            .forEach((attribute: any) => {
                if (context.selectedAttrsList.length && this.isAttributeValueIdentical(context, attribute)) {
                    const featureMap = this.spatialUtilService.getFeatureMap(this.spatialUtilService.getFeatures(context.featureLayer));

                    context.selectedAttrsList.forEach(attrs => {
                        const realValue = featureMap[attrs.id].attributes[attribute.prop];

                        if (attribute.values !== undefined && realValue !== null) {
                            attribute.values.forEach((lookup: any) => {
                                if (
                                    attribute.attributeType.includes('RADIO') &&
                                    (String(lookup.value) === String(realValue) ||
                                        (realValue && lookup.value && String(lookup.value).charAt(0) === String(realValue).charAt(0)))
                                ) {
                                    attribute.value = lookup.value;
                                } else if (String(lookup.id) === String(realValue)) {
                                    attribute.value = lookup;
                                } else if (
                                    attribute.dms &&
                                    attribute.attributeType.includes('DOMAIN') &&
                                    String(lookup.decimalDegrees) === String(realValue)
                                ) {
                                    attribute.value = lookup;
                                }
                            });
                        } else if (attribute.attributeType.includes('INTEGER')) {
                            // ND-44: display the area passed from polygon to the lot
                            if (attribute.featureType === 'lot' && attribute.prop === 'totalarea') {
                                attribute.value = this.spatialUtilService.parseFloat(attrs[attribute.prop]);
                            } else {
                                attribute.value = Number(realValue);
                            }
                        } else if (attribute.attributeType === 'BOOLEAN') {
                            attribute.value = realValue === 'YES';
                        } else {
                            attribute.value = realValue;
                        }

                        attribute.previousValue = realValue;
                    });

                    this.populateParentAttributeBasedOnCustomChildren(context, attribute);
                    this.checkChildrenAttributesDisplay(context, attribute);
                    this.populateAttributeBasedOnValidParentLookupIds(context, attribute);
                    this.cleanCustomChildrenAttributesBasedOnParent(attribute);
                    this.checkReadOnlyAttributeBasedOnValidParentLookupIds(context, attribute);
                } else if (typeAttribute === undefined || (typeAttribute && typeAttribute.value === undefined)) {
                    this.resetEditorAttributes(context, attribute);
                }
            });

        this.verifyPolygonUsage(context);
        this.removeInvalidAttributesByFeatureSubType(context, typeAttribute);
        this.setMandatoryAttributes(context, typeAttribute);
    }

    isAttributeValueIdentical(context: AttributeViewerContext, attribute: any) {
        console.debug(`${this.title} - isAttributeValueIdentical`);

        const firstFeatureAttributeValue = context.selectedAttrsList.length && context.selectedAttrsList[0][attribute.prop];
        return context.selectedAttrsList.every(attrs => attrs[attribute.prop] === firstFeatureAttributeValue);
    }

    verifyPolygonUsage(context: AttributeViewerContext) {
        console.debug(`${this.title} - verifyPolygonUsage`);

        if (context.tool.type === 'polygon') {
            const typeAttribute = context.editorAttributes.find((attribute: any) => attribute.prop === 'type');

            typeAttribute.editable = typeAttribute.editableOverridenValue ? typeAttribute.editableOverridenValue : typeAttribute.editable;

            context.selectedAttrsList.forEach(attrs => {
                if (attrs.lotid || attrs.roadid) {
                    typeAttribute.editableOverridenValue = typeAttribute.editable;
                    typeAttribute.editable = false;
                }
            });
        }
    }

    resetEditorAttributes(context: AttributeViewerContext, attribute?: any) {
        console.debug(`${this.title} - resetEditorAttributes`);

        if (attribute) {
            attribute.value = undefined;
            attribute.previousValue = undefined;
        } else {
            context.editorAttributes.forEach((attribute: any) => {
                attribute.value = undefined;
                attribute.previousValue = undefined;
            });
        }
    }

    saveEditor(context: AttributeViewerContext, editorForm: NgForm) {
        console.debug(`${this.title} - saveEditor`);

        if (context.selectedAttrsList.length > 0) {
            return this.mapToolsService.activate('ATTRIBUTE_EDITOR_APPLY', () => {
                editorForm.onSubmit(undefined);
                if (editorForm.valid) {
                    const editorAttributes = {};
                    const mandatoryAttributes = {};

                    const lookupObservables: Observable<any>[] = [];

                    const retainedProperties = context.attributes.filter(a => a.editable && !context.editorAttributes.some(ea => ea.prop === a.prop));
                    context.selectedAttrsList.forEach(sa => {
                        retainedProperties.forEach(ra => {
                            if (sa[ra.prop]) {
                                editorAttributes[ra.prop] = sa[ra.prop];
                                mandatoryAttributes[ra.prop] = true;
                            }
                        });
                    });

                    context.editorAttributes.forEach((attribute: any) => {
                        if (attribute.value !== undefined) {
                            const isAttributeUnique = this.isAttributeUnique(context, attribute);

                            if (attribute.parentAttribute === null || isAttributeUnique) {
                                if (attribute.value !== null && typeof attribute.value === 'object' && attribute.value.id) {
                                    editorAttributes[attribute.prop] = isAttributeUnique ? attribute.value.id : '[' + attribute.value.id + ']';

                                    // copy across additional attributes for any attribute with a customeservice defined
                                    if (attribute.customService) {
                                        lookupObservables.push(
                                            this.mapService
                                                .getCustomLookups(`${attribute.customService}/${this.planModelService.getGuid()}`, attribute)
                                                .pipe(
                                                    tap((data: any[]) => {
                                                        // find the lookup value
                                                        const customValue = data.find(item => item.id === attribute.value.id);

                                                        // copy across values that are not 'id' and exist as attributes in the model
                                                        if (customValue) {
                                                            for (const attr in customValue) {
                                                                if (attr !== 'id' && context.attributes.some(ca => ca.prop === attr.toLowerCase())) {
                                                                    editorAttributes[attr.toLowerCase()] = customValue[attr];
                                                                }
                                                            }
                                                        }
                                                    })
                                                )
                                        );
                                    }
                                } else {
                                    if (attribute.attributeType === 'BOOLEAN') {
                                        editorAttributes[attribute.prop] = attribute.value === 'Yes' || attribute.value === true ? 'YES' : 'NO';
                                    } else if (attribute.featureType === 'angle' && attribute.name === 'Angle' && attribute.value) {
                                        editorAttributes[attribute.prop] = attribute.value;
                                    } else {
                                        editorAttributes[attribute.prop] = attribute.value;
                                    }
                                }
                            } else if (attribute.showBasedOnParent) {
                                const parentAttribute = this.getParentAttribute(context, attribute.parentAttribute);

                                editorAttributes[attribute.prop] = '[' + parentAttribute.value.id + ']-' + attribute.value;
                            }

                            mandatoryAttributes[attribute.prop] = attribute.mandatory;
                        }
                    });

                    forkJoinDefaultIfEmpty(lookupObservables)
                        .pipe(
                            switchMap(() => {
                                const oldLot =
                                    context.tool.type === 'lot' && context.selectedAttrsList.length === 1 && context.selectedAttrsList[0].name;

                                return this.commitFeaturesAttributesUpdate(context, editorAttributes, mandatoryAttributes).pipe(
                                    tap(() => {
                                        // DCX-8067 update interest boundaries for edited lots
                                        // supersedes DCX-6774 implementation
                                        if (context.tool.type === 'lot' && context.selectedAttrsList.length === 1) {
                                            for (const [updatedAttributeName, updatedAttribute] of Object.entries(editorAttributes) as any[]) {
                                                if (updatedAttributeName === 'name') {
                                                    this.interestService.updateLotInterests([oldLot, updatedAttribute]);
                                                }
                                            }
                                        }
                                    })
                                );
                            })
                        )
                        .subscribe();
                }
            });
        } else {
            this.alertModalService.createAlertMessage('There is no feature selected!', 'Ok');
            return of(null);
        }
    }

    private setEdits(edits: any, featureClass: string, adds: any[], updates: any[], deletes: any[]) {
        const featureClassEdits = (edits[featureClass] = edits[featureClass] || {adds: null, updates: null, deletes: null});
        if (adds) {
            featureClassEdits.adds = featureClassEdits.adds || [];
            featureClassEdits.adds.push(...adds);
        }
        if (updates) {
            featureClassEdits.updates = featureClassEdits.updates || [];
            const updateIds = updates.map(u => u.attributes.id);
            featureClassEdits.updates.splice(
                0,
                featureClassEdits.updates.length,
                ...[...featureClassEdits.updates.filter((u: any) => !updateIds.includes(u.attributes.id)), ...updates]
            );
        }
        if (deletes) {
            featureClassEdits.deletes = featureClassEdits.deletes || [];
            featureClassEdits.deletes.push(...deletes);
        }
    }

    private commitFeaturesAttributesUpdate(context: AttributeViewerContext, editorAttributes: any, mandatoryAttributes: any) {
        console.debug(`${this.title} - commitFeaturesAttributesUpdate`);

        const featuresToBeUpdated = [];
        const interestIds = [];
        const roadIds = [];

        const edits: {lot?: {updates?: any[]}; road?: {updates?: any[]}} = {};

        context.selectedAttrsList.forEach(attrs => {
            const selectedFeature: {attributes: any; geometry?: any} = {attributes: {}};

            this.spatialUtilService
                .getGraphics(context.featureLayer)
                .filter((g: any) => attrs.id === g.attributes.id)
                .forEach((g: any) => {
                    this.copyLegacyAttributes(context, editorAttributes, g, selectedFeature);
                    selectedFeature.geometry = this.spatialUtilService.copyGeometry(g.geometry);
                });

            for (const [updatedAttributeName, updatedAttribute] of Object.entries(editorAttributes) as any[]) {
                if (
                    editorAttributes.type &&
                    updatedAttributeName !== 'type' &&
                    !context.attributes.some(a => a.prop === updatedAttributeName && this.isAttributeValidForFeatureSubType(editorAttributes.type, a))
                ) {
                    continue;
                }

                if (
                    typeof mandatoryAttributes[updatedAttributeName] === 'undefined' ||
                    mandatoryAttributes[updatedAttributeName] === false ||
                    (mandatoryAttributes[updatedAttributeName] === true && updatedAttribute !== null) ||
                    (mandatoryAttributes[updatedAttributeName] === true && updatedAttribute === null && updatedAttributeName === 'bearing')
                ) {
                    switch (updatedAttributeName) {
                        case 'bearing':
                            if (!selectedFeature.attributes.bearing && updatedAttribute === null) {
                                this.createBearing(context, selectedFeature);
                            } else if (context.selectedAttrsList.length === 1) {
                                selectedFeature.attributes.bearing = updatedAttribute;
                            }
                            break;
                        case 'anglerotationdms':
                            selectedFeature.attributes.anglerotationdms = updatedAttribute;
                            selectedFeature.attributes.anglerotation = this.angleUtilService.dmsToDd(updatedAttribute);
                            break;
                        case 'visible':
                            if (updatedAttribute && typeof updatedAttribute === 'string' && ['YES', 'NO'].includes(updatedAttribute.toUpperCase())) {
                                selectedFeature.attributes.visible = updatedAttribute.toUpperCase();
                            }
                            break;
                        case 'type':
                            {
                                const legalParcelTypeLookups: LegalParcelTypeLookup[] = this.lookupService.getLookups('legalParcelType');

                                if (attrs.lotid && [25, 26, 97].includes(updatedAttribute)) {
                                    const lot =
                                        edits.lot?.updates?.find((u: any) => u.attributes.id === selectedFeature.attributes.lotid) ||
                                        this.spatialUtilService.getLots().find((lot: any) => lot.attributes.id === selectedFeature.attributes.lotid);

                                    if (lot) {
                                        const annotations: any[] = this.spatialUtilService.getAnnotations();
                                        const lotAnnotations = annotations.filter(
                                            a => a.attributes.featuretype === 'lot' && a.attributes.featureid === lot.attributes.id
                                        );

                                        const lotUpdate = this.spatialUtilService.copyGraphic(lot);
                                        const polygonIds = this.spatialUtilService.parseIdsAttr(lot.attributes.polygonids);

                                        const polygonIdx = polygonIds.indexOf(selectedFeature.attributes.id);
                                        if (polygonIdx !== -1) {
                                            polygonIds.splice(polygonIdx, 1);
                                        }

                                        lotUpdate.attributes.polygonids = this.spatialUtilService.toIdsAttr(polygonIds);

                                        const polygons = this.spatialUtilService
                                            .getPolygons()
                                            .filter((p: any) => polygonIds.includes(p.attributes.id));
                                        this.featureAssociationService.setTotalAreaAndGeometry(lotUpdate, polygons);

                                        selectedFeature.attributes.lotid = null;
                                        this.setEdits(edits, 'lot', null, [lotUpdate], null);
                                        this.setEdits(edits, 'polygon', null, [selectedFeature], null);

                                        const lotAnnotationsToDelete = polygonIds.length
                                            ? lotAnnotations.filter(
                                                  a => a.attributes.childtype === 'polygon' && a.attributes.childid === selectedFeature.attributes.id
                                              )
                                            : lotAnnotations;
                                        if (lotAnnotationsToDelete.length) {
                                            this.setEdits(edits, 'annotation', null, null, lotAnnotationsToDelete);
                                        }
                                    }
                                }

                                if (
                                    attrs.roadid &&
                                    ['Road', 'Road Widening'].includes(attrs.type) &&
                                    !legalParcelTypeLookups.some(l => l.id === updatedAttribute && l.description === attrs.type)
                                ) {
                                    const road =
                                        (edits.road &&
                                            edits.road.updates &&
                                            edits.road.updates.find(r => r.attributes.id === selectedFeature.attributes.roadid)) ||
                                        this.spatialUtilService.getRoads().find((r: any) => r.attributes.id === selectedFeature.attributes.roadid);

                                    if (road) {
                                        const annotations = this.spatialUtilService.getAnnotations();
                                        const roadAnnotations = annotations.filter(
                                            (a: any) => a.attributes.featuretype === 'road' && a.attributes.featureid === road.attributes.id
                                        );

                                        const roadUpdate = new this.esriModuleService.Graphic(road.toJson());
                                        const polygonIds = this.spatialUtilService.parseIdsAttr(road.attributes.polygonids);

                                        const polygonIdx = polygonIds.indexOf(selectedFeature.attributes.id);
                                        if (polygonIdx !== -1) {
                                            polygonIds.splice(polygonIdx, 1);
                                        }

                                        roadUpdate.attributes.polygonids = this.spatialUtilService.toIdsAttr(polygonIds);

                                        const polygons = this.spatialUtilService
                                            .getPolygons()
                                            .filter((p: any) => polygonIds.includes(p.attributes.id));
                                        this.featureAssociationService.setTotalAreaAndGeometry(roadUpdate, polygons);

                                        selectedFeature.attributes.roadid = null;
                                        this.setEdits(edits, 'road', null, [roadUpdate], null);
                                        this.setEdits(edits, 'polygon', null, [selectedFeature], null);

                                        const roadAnnotationsToDelete =
                                            polygonIds.length && attrs.type === 'Road Widening'
                                                ? roadAnnotations.filter(
                                                      (a: any) =>
                                                          a.attributes.childtype === 'polygon' &&
                                                          a.attributes.childid === selectedFeature.attributes.id
                                                  )
                                                : !polygonIds.length
                                                  ? roadAnnotations
                                                  : [];

                                        if (roadAnnotationsToDelete.length) {
                                            this.setEdits(edits, 'annotation', null, null, roadAnnotationsToDelete);
                                        }
                                    }
                                }

                                if (context.tool.type === 'lot' && (attrs.roadid || editorAttributes.roadid)) {
                                    const selectedLot = this.spatialUtilService
                                        .getLots()
                                        .find((lot: any) => lot.attributes.id === selectedFeature.attributes.id);

                                    const roadAsLotAnnotationsToUpdate = this.spatialUtilService
                                        .getAnnotations()
                                        .filter(
                                            (a: any) => a.attributes.featuretype === 'lot' && a.attributes.featureid === selectedLot.attributes.id
                                        );
                                    this.annotationService.deleteAnnotations(roadAsLotAnnotationsToUpdate).subscribe();
                                }

                                selectedFeature.attributes[updatedAttributeName] = structuredClone(updatedAttribute);
                            }
                            break;
                        default:
                            selectedFeature.attributes[updatedAttributeName] = structuredClone(updatedAttribute);
                            break;
                    }
                }
            }

            const graphic = new this.esriModuleService.Graphic();
            graphic.setAttributes(selectedFeature.attributes);
            graphic.setGeometry(selectedFeature.geometry);

            // Update feature geometry if xy values have been changed
            if (context.selectedAttrsList.length === 1 && selectedFeature.geometry.type === 'point') {
                //check if any interest lot has to be updated
                this.spatialUtilService
                    .getInterests()
                    .filter(
                        (interest: any) =>
                            !interestIds.includes(interest.attributes.interestid) &&
                            interest.geometry &&
                            interest.geometry.rings &&
                            interest.geometry.rings.some((ring: any) =>
                                ring.some((point: any) => selectedFeature.geometry.x === point[0] || selectedFeature.geometry.y === point[1])
                            )
                    )
                    .forEach((interest: any) => interestIds.push(interest.attributes.interestid));

                //check if any road interest has to be updated
                this.spatialUtilService.getRoads().forEach((road: any) => {
                    road.geometry.rings.forEach((ring: any) => {
                        ring.forEach((coordinates: any[]) => {
                            if (selectedFeature.geometry.x === coordinates[0] || selectedFeature.geometry.y === coordinates[1]) {
                                if (!roadIds.includes(road.attributes.id)) {
                                    roadIds.push(road.attributes.id);
                                }
                            }
                        });
                    });
                });

                for (const [updatedAttributeKey, updatedAttribute] of Object.entries(editorAttributes) as any[]) {
                    if (updatedAttributeKey === 'x' && updatedAttribute !== graphic.attributes.origx) {
                        graphic.geometry.x = selectedFeature.attributes.x;
                        graphic.attributes.origx = selectedFeature.attributes.x;
                    } else if (updatedAttributeKey === 'y' && updatedAttribute !== graphic.attributes.origy) {
                        graphic.geometry.y = selectedFeature.attributes.y;
                        graphic.attributes.origy = selectedFeature.attributes.y;
                    }
                }
            }

            featuresToBeUpdated.push(graphic);
        });

        //Update total Lot if the updated line is a part of it
        if (context.tool.type === 'line') {
            featuresToBeUpdated.push(...this.findAssociatedTotalLotBoundaries(featuresToBeUpdated));
        }

        this.mapToolsService.setShortcutsEnabled(false);

        const editsKeys = Object.keys(edits);

        return forkJoinDefaultIfEmpty(
            editsKeys.map(fc => this.spatialUtilService.applyEdits(fc, edits[fc].adds, edits[fc].updates, edits[fc].deletes))
        ).pipe(
            switchMap(() => {
                return this.featureApplyUpdateService.updateFeaturesByType(context.featureLayer, featuresToBeUpdated).pipe(
                    switchMap((updatedFeatures: any[]) => {
                        if (updatedFeatures) {
                            console.debug(`${this.title} - commitFeaturesAttributesUpdate - Features updated`);
                            return this.updateFeatureAttributesInScope(context, featuresToBeUpdated).pipe(
                                tap(() => this.eventBus.cast('mapRefresh'))
                            );
                        } else {
                            console.debug(`${this.title} - commitFeaturesAttributesUpdate - Error updating features.`);
                            return of(null);
                        }
                    }),
                    switchMap(() => {
                        const observables: Observable<any>[] = [];
                        if (interestIds.length) {
                            observables.push(this.updateInterestFeatures(interestIds));
                        }
                        if (roadIds.length) {
                            observables.push(this.updateRoadFeatures(roadIds));
                        }
                        return forkJoinDefaultIfEmpty(observables);
                    })
                );
            }),
            tap(() => this.setShortcutsEnabled())
        );
    }

    private setShortcutsEnabled() {
        if (this.setShortcutsEnabledTimeout) {
            clearTimeout(this.setShortcutsEnabledTimeout);
        }
        this.setShortcutsEnabledTimeout = window.setTimeout(() => this.mapToolsService.setShortcutsEnabled(true), 500);
    }

    updateInterestFeatures(interestIds: any[]) {
        console.debug(`${this.title} - updateInterestFeatures`);

        const features = [];
        interestIds.forEach(interestId => {
            const feature = this.spatialUtilService.getInterestGraphics().find((g: any) => g.attributes.interestid === interestId);
            if (feature && feature.attributes) {
                this.interestService.populateInterestGeometry(feature);
                features.push(feature);
            }
        });

        return this.featureApplyUpdateService.updateFeaturesByType(this.spatialUtilService.getInterestMapLayer(), features).pipe(
            tap(() => {
                console.debug('Feature has been successfully updated!');
                this.spatialUtilService.getMapFeatureLayer('interest').redraw();
            })
        );
    }

    updateRoadFeatures(roadIds: any[]) {
        console.debug(`${this.title} - updateRoadFeatures`);

        const features = [];
        roadIds.forEach(roadId => {
            const feature = this.spatialUtilService.getRoadGraphics().find((f: any) => f.attributes.id === roadId);
            if (feature && feature.attributes) {
                const polygonIds = this.spatialUtilService.parseIdsAttr(feature.attributes.polygonids);
                const polygons = polygonIds.length
                    ? this.spatialUtilService.getPolygonGraphics().filter((p: any) => polygonIds.includes(p.attributes.id))
                    : [];

                const polygonGeometries = polygons.map((polygon: any) => {
                    const lineIds = this.spatialUtilService.parseIdsAttr(polygon.attributes.lineids);
                    const lines = this.spatialUtilService.getLineGraphics().filter((l: any) => lineIds.includes(l.attributes.id));
                    const polygonGeometry = new this.esriModuleService.geometry.Polygon();
                    polygonGeometry.addRing(this.polygonUtilService.getPolygonRing(lines));
                    return polygonGeometry;
                });

                feature.geometry = this.spatialUtilService.polygonGeometryUnion(polygonGeometries);

                features.push(feature);
            }
        });

        return this.featureApplyUpdateService.updateFeaturesByType(this.spatialUtilService.getRoadMapLayer(), features).pipe(
            tap(() => {
                console.debug('Feature has been successfully updated!');
                this.spatialUtilService.getMapFeatureLayer('road').redraw();
            })
        );
    }

    private copyLegacyAttributes(context: AttributeViewerContext, editorAttributes: any, oldFeature: any, newFeature: any) {
        console.debug(`${this.title} - copyLegacyAttributes`);

        const attributes = structuredClone(oldFeature.attributes);

        context.attributes.forEach((attribute: any) => {
            Object.keys(oldFeature.attributes).forEach((k: string) => {
                if (attribute.prop === k && Object.hasOwn(attributes, attribute.prop) && attribute.prop !== 'x' && attribute.prop !== 'y') {
                    if (editorAttributes.type && !this.isAttributeValidForFeatureSubType(editorAttributes.type, attribute)) {
                        attributes[attribute.prop] = null;
                    }
                }
            });
        });

        newFeature.attributes = attributes;
    }

    createBearing(context: AttributeViewerContext, newFeature: any) {
        console.debug(`${this.title} - createBearing`);

        context.attributes.forEach((attribute: any) => {
            if (
                attribute.name === 'Bearing' &&
                Object.hasOwn(newFeature.attributes, 'bearing') &&
                Object.hasOwn(newFeature.attributes, 'type') &&
                attribute.subTypesMandatoryAtLodgement &&
                attribute.subTypesMandatoryAtLodgement.split(',').some((subType: string) => subType == newFeature.attributes['type'])
            ) {
                if (!newFeature.attributes.bearing) {
                    const bearing = this.lineService.calculateBearing(
                        newFeature.geometry.paths[0][0][0],
                        newFeature.geometry.paths[0][0][1],
                        newFeature.geometry.paths[0][1][0],
                        newFeature.geometry.paths[0][1][1]
                    );

                    newFeature.attributes.bearing = this.angleUtilService.ddToDms(bearing);
                }
            }
        });
    }

    private findAssociatedTotalLotBoundaries(linesToUpdate: any[]) {
        const totalLotBoundaryLineType = 95;

        const linesToUpdateMap = linesToUpdate.reduce((acc, l) => {
            acc[l.attributes.id] = l;
            return acc;
        }, {});

        const lineMap = this.spatialUtilService
            .getLines()
            .filter((l: any) => l.attributes.type !== totalLotBoundaryLineType)
            .reduce((acc: any, l: any) => {
                acc[l.attributes.id] = linesToUpdateMap[l.attributes.id] || l;
                return acc;
            }, {});

        return this.spatialUtilService.getLines().filter((l: any) => {
            const lineIds = l.attributes.type === totalLotBoundaryLineType ? this.spatialUtilService.parseIdsAttr(l.attributes.lineids) : [];
            if (lineIds.some(lineId => linesToUpdateMap[lineId])) {
                l.attributes.distance = lineIds
                    .filter(lineId => lineMap[lineId])
                    .map(lineId => lineMap[lineId].attributes.distance)
                    .reduce((totalDistance, distance) => totalDistance + distance);
                return l;
            }
        });
    }

    private updateFeatureAttributesInScope(context: AttributeViewerContext, features: any[]) {
        console.debug(`${this.title} - updateFeatureAttributesInScope`);

        return this.attributeViewerTranslationService.translateAttributeValues(context, features).pipe(
            tap((translatedFeatures: any[]) => {
                translatedFeatures.forEach(translatedFeature => {
                    context.features
                        .filter((feature: any) => translatedFeature.attributes.id === feature.attributes.id)
                        .forEach((feature: any) => Object.assign(feature.attributes, translatedFeature.attributes));

                    context.attrsList
                        .filter(attrs => translatedFeature.attributes.id === attrs.id)
                        .forEach(attrs => Object.assign(attrs, translatedFeature.attributes));

                    context.selectedAttrsList
                        .filter(attrs => translatedFeature.attributes.id === attrs.id)
                        .forEach(attrs => Object.assign(attrs, translatedFeature.attributes));
                });
            })
        );
    }

    private removeInvalidAttributesByFeatureSubType(context: AttributeViewerContext, typeAttribute: any) {
        console.debug(`${this.title} - removeInvalidAttributesByFeatureSubType`);

        if (context.selectedAttrsList.length && (typeAttribute || context.editorAttributes.find(v => v.prop === 'type'))) {
            const selectedFeatureTypes = [];

            if (!typeAttribute) {
                const featureMap = this.spatialUtilService.getFeatureMap(this.spatialUtilService.getFeatures(context.featureLayer));
                selectedFeatureTypes.push(
                    ...context.selectedAttrsList.map(v => featureMap[v.id].attributes.type).filter((v, i, a) => a.indexOf(v) === i)
                );
            } else if (typeAttribute.value) {
                selectedFeatureTypes.push(String(typeAttribute.value.id));
            }

            context.editorAttributes = context.editorAttributes.filter(v =>
                selectedFeatureTypes.every(selectedFeatureType => this.isAttributeValidForFeatureSubType(selectedFeatureType, v))
            );
        }
    }

    private isAttributeValidForFeatureSubType(typeAttribute: any, attribute: any) {
        console.debug(`${this.title} - isAttributeValidForFeatureSubType - attribute:`, attribute.prop);

        if (attribute.validSubTypeIds) {
            const validSubTypeIds = attribute.validSubTypeIds.split(',');
            if (validSubTypeIds.length > 0) {
                return validSubTypeIds.some((subType: string) => String(typeAttribute) === subType);
            }
        }

        return true;
    }

    private setMandatoryAttributes(context: AttributeViewerContext, typeAttribute: any) {
        console.debug(`${this.title} - setMandatoryAttributes`);

        if (!typeAttribute) {
            typeAttribute = context.editorAttributes.find(attribute => attribute.prop === 'type');
        }

        if (typeAttribute) {
            if (typeAttribute.value) {
                context.editorAttributes.forEach(attribute => {
                    if (this.shouldAttributeBeMandatoryForFeatureSubType(typeAttribute, attribute)) {
                        attribute.mandatory = true;
                    }
                });
            }
        } else {
            context.editorAttributes.forEach(attribute => {
                if (attribute.subTypesMandatoryAtLodgement === 'TRUE') {
                    attribute.mandatory = true;
                }
            });
        }
    }

    private shouldAttributeBeMandatoryForFeatureSubType(typeAttribute: any, attribute: any) {
        console.debug(`${this.title} - shouldAttributeBeMandatoryForFeatureSubType - attribute:`, attribute.prop);

        return attribute.subTypesMandatoryAtLodgement?.split(',').some((subType: string) => String(typeAttribute.value.id) === subType);
    }

    attributeSelectorChanged(context: AttributeViewerContext, selectedAttribute: any) {
        console.debug(`${this.title} - attributeSelectorChanged`);

        const observable =
            selectedAttribute.prop === 'type'
                ? this.setupEditor(context, selectedAttribute).pipe(
                      tap(() => {
                          let typeAttributeIndex = undefined;

                          context.editorAttributes.forEach(function (attribute, index) {
                              if (attribute.prop === 'type') {
                                  typeAttributeIndex = index;
                              }
                          });

                          const previousParcelTypeSelection = context.editorAttributes[typeAttributeIndex];

                          // Put type attribute back as it has been reset by the setupEditor function
                          context.editorAttributes[typeAttributeIndex] = selectedAttribute;

                          const acquisitionTransferActionAttr = context.editorAttributes.find(a => a.prop === 'acquisitiontransferaction');
                          if (acquisitionTransferActionAttr && acquisitionTransferActionAttr.values && acquisitionTransferActionAttr.values.length) {
                              acquisitionTransferActionAttr.values = this.filterLookupValuesBySelectedParcelTypeId(
                                  context,
                                  acquisitionTransferActionAttr,
                                  selectedAttribute.value.id
                              );
                              if (previousParcelTypeSelection !== selectedAttribute) {
                                  acquisitionTransferActionAttr.value = acquisitionTransferActionAttr.values[0];
                              }

                              if (!acquisitionTransferActionAttr.values || !acquisitionTransferActionAttr.values.length) {
                                  context.editorAttributes.splice(context.editorAttributes.indexOf(acquisitionTransferActionAttr), 1);
                              }
                          }
                      })
                  )
                : of(null);

        return observable.pipe(
            tap(() => {
                this.checkChildrenAttributesDisplay(context, selectedAttribute);
                this.resetChildrenAttributesBasedOnParent(context, selectedAttribute);
                this.populateAttributeBasedOnValidParentLookupIds(context, selectedAttribute);
                this.checkReadOnlyAttributeBasedOnValidParentLookupIds(context, selectedAttribute);
            })
        );
    }

    private isAttributeUnique(context: AttributeViewerContext, attributeParam: any) {
        console.debug(`${this.title} - isAttributeUnique`);

        return context.attributes.filter(attribute => attribute.prop === attributeParam.prop).length === 1;
    }

    private checkChildrenAttributesDisplay(context: AttributeViewerContext, attributeParam: any) {
        console.debug(`${this.title} - checkChildrenAttributesDisplay`);

        context.editorAttributes
            .filter(attribute => attribute.parentAttribute !== null && attribute.parentAttribute === attributeParam.prop)
            .forEach(attribute => (attribute.showBasedOnParent = this.showAttributeBasedOnValidParentLookupIds(context, attribute)));
    }

    private showAttributeBasedOnValidParentLookupIds(context: AttributeViewerContext, attribute: any) {
        console.debug(`${this.title} - showAttributeBasedOnValidParentLookupIds`);

        const parentAttribute = this.getParentAttribute(context, attribute.parentAttribute);

        if (parentAttribute) {
            return attribute.validParentLookupIds.split(',').some((parentLookupId: string) => parentLookupId === parentAttribute.value.id);
        }

        return false;
    }

    private getParentAttribute(context: AttributeViewerContext, property: any) {
        console.debug(`${this.title} - getParentAttribute`);

        const parentAttribute = context.editorAttributes.find(attribute => attribute.prop === property);

        return parentAttribute?.value?.id ? parentAttribute : undefined;
    }

    private checkReadOnlyAttributeBasedOnValidParentLookupIds(context: AttributeViewerContext, attributeParam: any) {
        console.debug(`${this.title} - checkReadOnlyAttributeBasedOnValidParentLookupIds`);

        if (attributeParam.value && attributeParam.parentAttribute === null) {
            context.editorAttributes
                .filter(attribute => attribute.parentAttribute !== null && attribute.parentAttribute === attributeParam.prop)
                .forEach(attribute => {
                    if (attribute.parentLookupIdsEditable === null) {
                        attribute.readyOnlyBasedOnParent = false;
                    } else {
                        const validParentLookupIds = attribute.parentLookupIdsEditable
                            .split(',')
                            .filter((parentLookupId: string) => parentLookupId === attributeParam.value.id);

                        attribute.readyOnlyBasedOnParent = validParentLookupIds.length === 0;
                    }
                });
        }
    }

    private populateParentAttributeBasedOnCustomChildren(context: AttributeViewerContext, attributeParam: any) {
        console.debug(`${this.title} - populateParentAttributeBasedOnCustomChildren`);

        const foundChildrenAttributes = context.editorAttributes.some(attribute => {
            return attribute.parentAttribute !== null && attribute.parentLookupProperty !== null && attribute.parentAttribute === attributeParam.prop;
        });

        if (
            attributeParam.parentAttribute === null &&
            attributeParam.attributeType === 'DOMAIN' &&
            attributeParam.lookupService !== null &&
            !attributeParam.value &&
            attributeParam.previousValue &&
            !this.isNumber(attributeParam.previousValue) &&
            foundChildrenAttributes
        ) {
            let lookupId = undefined;
            attributeParam.previousValue.split(',').forEach((v: string) => (lookupId = this.getLookupIdBasedOnCustomValue(v)));

            if (lookupId) {
                attributeParam.value = attributeParam.values.find((lookup: any) => lookup.id === lookupId);
            }
        }
    }

    private resetChildrenAttributesBasedOnParent(context: AttributeViewerContext, attributeParam: any) {
        console.debug(`${this.title} - resetChildrenAttributesBasedOnParent`);

        context.editorAttributes
            .filter(attribute => attribute.parentAttribute !== null && attribute.parentAttribute === attributeParam.prop)
            .forEach(attribute => (attribute.value = undefined));
    }

    private cleanCustomChildrenAttributesBasedOnParent(attributeParam: any) {
        console.debug(`${this.title} - cleanCustomChildrenAttributesBasedOnParent`);

        if (attributeParam.parentAttribute !== null && attributeParam.value) {
            attributeParam.value = this.removeLookupIdBasedOnCustomValue(attributeParam.value);
        }
    }

    private populateAttributeBasedOnValidParentLookupIds(context: AttributeViewerContext, attributeParam: any) {
        console.debug(`${this.title} - populateAttributeBasedOnValidParentLookupIds`);

        if (attributeParam.value) {
            context.editorAttributes
                .filter(a => a.parentAttribute !== null && a.parentLookupProperty !== null && a.parentAttribute === attributeParam.prop)
                .forEach(a => (a.value = attributeParam.value[a.parentLookupProperty]));
        }
    }

    private removeLookupIdBasedOnCustomValue(attributeValue: any) {
        console.debug(`${this.title} - removeLookupIdBasedOnCustomValue`);

        let customValue = attributeValue;

        if (attributeValue.length > 2 && attributeValue.includes('[') && attributeValue.includes(']') && attributeValue.includes('-')) {
            customValue = attributeValue.substring(0, attributeValue.indexOf('-') + 1);
            customValue = attributeValue.replace(customValue, '');
        }

        return customValue;
    }

    private getLookupIdBasedOnCustomValue(attributeValue: any) {
        console.debug(`${this.title} - getLookupIdBasedOnCustomValue`);

        let lookupId = undefined;

        if (attributeValue.length > 2 && attributeValue.includes('[') && attributeValue.includes(']')) {
            lookupId = attributeValue.substring(0, attributeValue.indexOf(']') + 1);
            lookupId = lookupId.replace(/[[\]]/g, '');
        } else if (this.isNumber(attributeValue)) {
            lookupId = attributeValue;
        }

        return lookupId;
    }

    private isNumber(value: any) {
        return !isNaN(parseFloat(value)) && isFinite(value);
    }

    setContext(context: AttributeViewerContext) {
        this.context = context;
        if (['affectedLot', 'interest'].includes(this.context.tool.type)) {
            this.mapToolsService.deactivate('ATTRIBUTE_EDITOR_WINDOW');
        }
    }

    private isDisabled() {
        return (
            this.planModelService.readonly ||
            (this.context?.editAttributesIsDisabled && this.context.editAttributesIsDisabled()) ||
            !this.permissionsService.hasEditPermission(this.permissions)
        );
    }
}
